'use client';

import type { ReactNode } from 'react';
import { useState, useMemo, useCallback } from 'react';
import Cookies from 'js-cookie';

import { createSafeContext } from '@/utils/react/createSafeContext';
import { WISHLIST_GUEST_USER_COOKIE_KEY } from '@/constants/browser/cookie';
import { CustomerKeyType } from '@/constants/wishlist/wishlist';

interface WishlistCustomerKeyContextValue {
  customerKey: string | undefined;
  customerKeyType: CustomerKeyType;
  setCustomerKey: (customerKey: string) => void;
}

const [WishlistCustomerKeyContextProvider, useWishlistCustomerKeyContext] =
  createSafeContext<WishlistCustomerKeyContextValue>('WishlistCustomerKey');

interface WishlistCustomerKeyProviderProps {
  initialCustomerKey?: string;
  children: ReactNode;
}

export const WishlistCustomerKeyProvider = ({ initialCustomerKey, children }: WishlistCustomerKeyProviderProps) => {
  const [wishlistKey, setWishlistKey] = useState<{
    customerKey?: string;
  }>({
    customerKey: initialCustomerKey,
  });

  const setCustomerKey = useCallback((customerKey: string) => {
    setWishlistKey((prev) => ({
      ...prev,
      customerKey,
    }));
    Cookies.set(WISHLIST_GUEST_USER_COOKIE_KEY, customerKey);
  }, []);

  const contextValue = useMemo(
    () => ({
      customerKey: wishlistKey.customerKey,
      customerKeyType: wishlistKey.customerKey ? CustomerKeyType.CUSTOMER : CustomerKeyType.GUEST,
      setCustomerKey,
    }),
    [wishlistKey.customerKey, setCustomerKey],
  );

  return <WishlistCustomerKeyContextProvider value={contextValue}>{children}</WishlistCustomerKeyContextProvider>;
};

export const useWishlistCustomerKey = useWishlistCustomerKeyContext;
